import {t, useLanguage, interfaceLocale} from './i18n';
import {businessZone, localInstant, wallDate, wallToInstant} from './utils/businessTime';
// src/MyBookings.jsx
// R33.2: carga y acciones de reservas exclusivamente por callables autenticados:
// - getUserBookings es la única fuente de reservas del cliente
// - cancelUserBooking y requestUserBookingChange son las únicas escrituras
// - "Cambiar Reserva" abre un modal accesible para re-agendar (fecha/hora)
// - La duración NO se puede editar. En el modal se muestra junto al nombre del servicio como "- 60 minutos".
// - Validación: no se puede seleccionar horarios en los que el trabajador no está disponible.
// - Validación: sólo intervalos de 30 minutos.
// - Validación: no se puede reprogramar a una fecha/hora pasada (ni hoy si la hora ya pasó).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { X, Repeat } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import './MyBookings.css';
import fsClient from './data/firestoreClient';
import { isIosCapacitor } from './utils/nativeHttp';
import { getManualAuth, isManualAuthLikelyValid } from './utils/manualAuth';
import { callCallableNativeSafe } from './utils/callableNativeSafe';
import BookingPaymentRecovery from './components/booking/BookingPaymentRecovery';
import EventQuoteCard from './components/events/EventQuoteCard';
import EventPriceChangeCard from './components/events/EventPriceChangeCard';
import { eventBookingProgress } from './utils/eventBookingFlow';
import { loadBookingPageOnce } from './utils/bookingPageRequests';
import { activateBookingMemory, clearBookingMemory, bookingMemoryKey, readBookingMemory, writeBookingMemory } from './utils/bookingViewMemory';

function normalizeString(v) { return v == null ? '' : String(v).trim().toLowerCase(); }

// LOOP MYBOOKINGS R33.2 — authenticated callable-only booking access.

function getFirestoreishDate(value) {
  try {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value.toDate === 'function') {
      const date = value.toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date;
    }
    if (typeof value === 'object') {
      const iso = value.iso || value.timestampValue || value.dateValue || value.value || value._iso || value.__iso;
      if (iso) {
        const date = new Date(iso);
        if (!Number.isNaN(date.getTime())) return date;
      }
      const seconds = value.seconds ?? value._seconds;
      const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
      if (seconds !== undefined && seconds !== null) {
        const date = new Date(Number(seconds) * 1000 + Math.floor(Number(nanos || 0) / 1000000));
        if (!Number.isNaN(date.getTime())) return date;
      }
    }
  } catch (_) {}
  return null;
}

// LOOP iOS / Apple auth helper
// En iOS usamos manual-auth porque Firebase Web Auth puede no hidratar currentUser dentro de WKWebView.
// Apple puede no traer email en el objeto superior, así que extraemos identidad desde raw/idToken también.
function decodeLoopJwtPayload(token) {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    b64 = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
    return JSON.parse(atob(b64));
  } catch (_) {
    return null;
  }
}

function uniqueNormalizedStrings(values = []) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const clean = normalizeString(value);
    if (!clean || seen.has(clean)) return;
    seen.add(clean);
    out.push(clean);
  });
  return out;
}

function manualAuthToBookingsUser(ma) {
  try {
    if (!ma || !isManualAuthLikelyValid(ma)) return null;
    const raw = ma.raw && typeof ma.raw === 'object' ? ma.raw : {};
    const idToken = ma.idToken || raw.idToken || raw.id_token || '';
    const payload = decodeLoopJwtPayload(idToken) || {};
    const firebaseIdentityEmails = Array.isArray(payload?.firebase?.identities?.email)
      ? payload.firebase.identities.email
      : [];
    const providerInfo = Array.isArray(raw.providerUserInfo) ? raw.providerUserInfo : [];

    const uid = String(
      ma.uid ||
      ma.localId ||
      ma.userId ||
      raw.localId ||
      raw.local_id ||
      raw.user_id ||
      payload.user_id ||
      payload.uid ||
      payload.sub ||
      ''
    ).trim();

    const emailAliases = uniqueNormalizedStrings([
      ma.email,
      ma.userEmail,
      raw.email,
      payload.email,
      ...(providerInfo.map((item) => item?.email)),
      ...firebaseIdentityEmails,
    ]);

    const displayName = String(
      ma.displayName ||
      ma.name ||
      raw.displayName ||
      raw.name ||
      payload.name ||
      ''
    ).trim();

    if (!uid && !emailAliases.length) return null;

    return {
      uid,
      email: emailAliases[0] || '',
      emailAliases,
      displayName,
      name: displayName,
      source: ma.source || raw.providerId || payload?.firebase?.sign_in_provider || 'manual-auth',
    };
  } catch (_) {
    return null;
  }
}

function getEffectiveBookingsUser(currentUser = null) {
  try {
    if (!isIosCapacitor()) return currentUser;

    const manualUser = manualAuthToBookingsUser(getManualAuth());
    if (!manualUser) return currentUser;

    if (!currentUser) return manualUser;

    const emailAliases = uniqueNormalizedStrings([
      currentUser.email,
      ...(Array.isArray(currentUser.emailAliases) ? currentUser.emailAliases : []),
      manualUser.email,
      ...(Array.isArray(manualUser.emailAliases) ? manualUser.emailAliases : []),
    ]);

    return {
      ...currentUser,
      uid: currentUser.uid || manualUser.uid,
      email: currentUser.email || manualUser.email || emailAliases[0] || '',
      emailAliases,
      displayName: currentUser.displayName || currentUser.name || manualUser.displayName || '',
      name: currentUser.name || currentUser.displayName || manualUser.name || '',
      source: manualUser.source || currentUser.source,
    };
  } catch (_) {
    return currentUser;
  }
}

function bookingsUserIdentityKey(user = null) {
  if (!user) return '';
  const uid = String(user.uid || '').trim();
  const email = normalizeString(user.email || (Array.isArray(user.emailAliases) ? user.emailAliases[0] : ''));
  return `${uid}|${email}`;
}

const MYBOOKINGS_PAGE_SIZE = 20;

function emptyBookingViewState() {
  return {
    items: [],
    pageInfo: {
      pageSize: MYBOOKINGS_PAGE_SIZE,
      returned: 0,
      hasMore: false,
      nextCursor: null,
      canonicalScanned: 0,
      legacyScanned: 0,
      legacyFallback: false,
      degraded: false,
    },
    loading: false,
    loadingMore: false,
    loaded: false,
    error: null,
  };
}

function bookingRowKey(booking = {}) {
  return String(
    booking.path ||
    booking._path ||
    `${booking.businessId || booking.businessID || 'nb'}/${booking.id || booking.bookingId || ''}`
  ).trim();
}

function mergeBookingRows(current = [], incoming = []) {
  const map = new Map();
  [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])].forEach((booking) => {
    const key = bookingRowKey(booking);
    if (key) map.set(key, booking);
  });
  return Array.from(map.values());
}

function bookingCountLabel(viewState = {}) {
  if (!viewState.loaded || (!viewState.items?.length && viewState.loadingMore)) return '—';
  const count = Array.isArray(viewState.items) ? viewState.items.length : 0;
  return viewState?.pageInfo?.hasMore ? `${count}+` : String(count);
}

export function parseScheduled(booking = {}) {
  if (!booking) return null;
  const directCandidates = [
    booking.scheduledAt,
    booking.scheduled_at,
    booking.scheduled,
    booking.startAt,
    booking.startsAt,
    booking.dateTime,
    booking.start,
    booking.starts_at,
    booking.timeSlot,
  ];
  for (const candidate of directCandidates) {
    const d = getFirestoreishDate(candidate);
    if (d) return d;
  }

  try {
    const dateField = booking.date || booking.bookingDate || booking.scheduledDate || null;
    const timeField = booking.time || booking.bookingTime || booking.scheduledTime || null;
    if (dateField) {
      let iso = String(dateField);
      if (timeField) {
        let t = String(timeField).trim();
        if (/^\d{1,2}:\d{2}$/.test(t)) t = t + ':00';
        iso = `${iso}T${t}`;
      } else {
        iso = `${iso}T00:00:00`;
      }
      const d = /^\d{4}-\d{2}-\d{2}$/.test(String(dateField)) ? localInstant(String(dateField), String(timeField || '00:00').slice(0,5).padStart(5,'0'), businessZone(booking)) : new Date(iso);
      if (!isNaN(d.getTime())) return d;
    }
  } catch (e) {}

  return null;
}

function myBookingMembershipSummary(booking = {}) {
  const applied = booking?.membershipApplied === true || booking?.membershipBenefitApplied === true || Boolean(booking?.customerMembershipId || booking?.membershipId);
  if (!applied) return null;
  const planName = String(booking.membershipPlanName || booking.membershipBenefitPlanName || "Membresía").trim() || "Membresía";
  const type = String(booking.membershipBenefitType || (Number(booking.membershipCreditsUsed || 0) > 0 ? "credit" : "discount")).toLowerCase();
  const credits = Math.max(0, Number(booking.membershipCreditsUsed || 0) || 0);
  const discount = Math.max(0, Number(booking.membershipDiscountPercent || 0) || 0);
  const processed = booking.membershipBenefitProcessed === true || String(booking.membershipBenefitStatus || "").toLowerCase() === "processed" || String(booking.membershipBenefitStatus || "").toLowerCase() === "credit_used";
  const title = processed ? (type === "discount" ? "Descuento de membresía aplicado" : "Crédito de membresía usado") : "Reserva con membresía";
  const detail = type === "discount" ? `${discount}% de descuento · ${planName}` : `${credits || 1} crédito · ${planName}`;
  const note = processed ? "El beneficio ya fue procesado por el negocio." : "El crédito se descontará cuando el negocio complete/pague el servicio.";
  return { title, detail, note, processed };
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'pending_payment') return <span className="mb-status mb-status-pending">{t("Pendiente de pago")}</span>;
  if (s === 'confirmed' || s === 'confirmada' || s === 'confirmado' || s === 'accepted' || s === 'approved') {
    return <span className="mb-status mb-status-confirmed">{t("Confirmada")}</span>;
  }
  if (s === 'pending' || s === 'pendiente' || s === 'requested' || s === 'awaiting_approval') {
    return <span className="mb-status mb-status-pending">{t("Pendiente")}</span>;
  }
  if (s === 'cancelled' || s === 'cancelada' || s === 'cancelado' || s === 'canceled' || s === 'rejected') {
    return <span className="mb-status mb-status-cancelled">{t("Cancelada")}</span>;
  }
  if (s === 'no_show' || s === 'no-show' || s === 'noshow' || s === 'missed' || s === 'did_not_show') {
    return <span className="mb-status mb-status-no-show">{t("No asistió")}</span>;
  }
  if (s === 'attended' || s === 'completed' || s === 'complete' || s === 'done' || s === 'finished' || s === 'finalizada') {
    return <span className="mb-status mb-status-completed">{t("Realizada")}</span>;
  }
  return <span className="mb-status mb-status-neutral">{status || '—'}</span>;
}

function formatDateTimeString(d) {
  if (!d) return 'Fecha no disponible';
  try {
    return d.toLocaleString(interfaceLocale(), { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return String(d);
  }
}

function formatBookingDateParts(d, booking = {}) {
  if (!d) return { dateLabel: 'Fecha no disponible', timeLabel: '—' };
  try {
    const dateLabel = d.toLocaleDateString(interfaceLocale(), {timeZone: businessZone(booking),
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timeLabel = d.toLocaleTimeString(interfaceLocale(), {timeZone: businessZone(booking),
      hour: 'numeric',
      minute: '2-digit',
    });
    return {
      dateLabel: dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1),
      timeLabel,
    };
  } catch (_) {
    return { dateLabel: formatDateTimeString(d), timeLabel: '' };
  }
}



function mbFirstLetter(value = "") {
  const text = String(value || "").trim();
  return text ? text.charAt(0).toUpperCase() : "L";
}

function mbPickLogoFrom(source = {}) {
  if (!source || typeof source !== "object") return "";
  return (
    source.logoUrl ||
    source.logoURL ||
    source.logo ||
    source.businessLogo ||
    source.businessLogoUrl ||
    source.profileLogoUrl ||
    source.imageLogoUrl ||
    source.brandLogoUrl ||
    source.publicLogoUrl ||
    source.photoURL ||
    source.photoUrl ||
    source.avatarUrl ||
    source.coverLogoUrl ||
    source?.business?.logoUrl ||
    source?.business?.logo ||
    source?.business?.photoURL ||
    ""
  );
}

function mbBusinessDisplayName(booking = {}, profile = {}) {
  return (
    profile.name ||
    profile.businessName ||
    booking.businessName ||
    booking.business ||
    booking.companyName ||
    "Negocio"
  );
}

function mbServiceDisplayName(booking = {}) {
  return booking.serviceName || booking.title || booking.service || booking.serviceTitle || "Servicio";
}

function mbCleanText(value = "") {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    const parts = [
      value.label,
      value.name,
      value.title,
      value.area,
      value.city,
      value.district,
      value.neighborhood,
      value.addressLine,
      value.address,
      value.formattedAddress,
      value.description,
    ].filter((x) => typeof x === "string" && x.trim());
    return parts[0] || "";
  }
  return "";
}

function mbBusinessArea(booking = {}, profile = {}) {
  return (
    mbCleanText(profile.location) ||
    mbCleanText(profile.address) ||
    mbCleanText(profile.city) ||
    mbCleanText(booking.businessLocation) ||
    mbCleanText(booking.location) ||
    mbCleanText(booking.address) ||
    ""
  );
}


/* Accessible modal used for change reservation UI */
function AccessibleModal({ open, onClose, labelledBy, children, width = 560 }) {
  const dialogRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable && focusable.length > 0) focusable[0].focus();
      else dialog.focus();
    }, 0);

    function onKey(e) {
      if (e.key === "Escape") { e.stopPropagation(); onClose && onClose(); }
      if (e.key === "Tab") {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(el => !el.disabled);
        if (focusable.length === 0) { e.preventDefault(); return; }
        const idx = focusable.indexOf(document.activeElement);
        if (e.shiftKey) {
          if (idx === 0) { focusable[focusable.length - 1].focus(); e.preventDefault(); }
        } else {
          if (idx === focusable.length - 1) { focusable[0].focus(); e.preventDefault(); }
        }
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      try { if (lastFocusedRef.current && lastFocusedRef.current.focus) lastFocusedRef.current.focus(); } catch (_) {}
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="mb-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        className="mb-modal-panel"
        onMouseDown={(e) => e.stopPropagation()}
        tabIndex={-1}
        style={{ maxWidth: width }}
      >
        <div className="mb-modal-grabber" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}

/* Utility helpers used for availability checks */
function parseTimeToMinutes(t) {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]), mm = Number(m[2]);
  return hh * 60 + mm;
}
function minutesFromDate(d) { return d.getHours() * 60 + d.getMinutes(); }
function addMinutes(d, mins) { return new Date(d.getTime() + mins * 60000); }
function rangesOverlap(aStart, aEnd, bStart, bEnd) { return aStart < bEnd && bStart < aEnd; }

/**
 * isEmployeeAvailableAt - heuristics:
 * - If no employee doc or no recognizable availability fields -> return { ok: true }
 * - Look for employee.availability (array), employee.workingHours, employee.weeklyAvailability, employee.hours
 * - Also ensure no conflicting booking for that employee exists in bookingsArray (overlap check)
 */
export function isEmployeeAvailableAt(employee, datetime, durationMin, bookingsArray = [], bookingToExcludeId = null, bookingContext = {}) {
  if (!employee) return { ok: true, reason: null };

  const dayIndex = datetime.getDay();
  const startMin = minutesFromDate(datetime);
  const endMin = startMin + (Number(durationMin || 60) || 60);

  const testWindows = (windows = []) => {
    for (const w of windows) {
      let days = [];
      if (Array.isArray(w.days) && w.days.length) {
        days = w.days.map(x => {
          if (typeof x === 'number') return x % 7;
          const s = String(x).toLowerCase();
          if (['sun','domingo','dom'].includes(s)) return 0;
          if (['mon','lunes','lun'].includes(s)) return 1;
          if (['tue','martes','mar'].includes(s)) return 2;
          if (['wed','miercoles','mie','mié'].includes(s)) return 3;
          if (['thu','jueves','jue'].includes(s)) return 4;
          if (['fri','viernes','vie'].includes(s)) return 5;
          if (['sat','sabado','sab'].includes(s)) return 6;
          const n = Number(x);
          if (!Number.isNaN(n)) return n % 7;
          return null;
        }).filter(v => v !== null);
      } else if (typeof w.day !== 'undefined' && w.day !== null) {
        const v = w.day;
        if (typeof v === 'number') days = [v % 7];
        else {
          const s = String(v).toLowerCase();
          if (['sun','domingo','dom'].includes(s)) days = [0];
          if (['mon','lunes','lun'].includes(s)) days = [1];
          if (['tue','martes','mar'].includes(s)) days = [2];
          if (['wed','miercoles','mie','mié'].includes(s)) days = [3];
          if (['thu','jueves','jue'].includes(s)) days = [4];
          if (['fri','viernes','vie'].includes(s)) days = [5];
          if (['sat','sabado','sab'].includes(s)) days = [6];
        }
      } else {
        days = [0,1,2,3,4,5,6];
      }

      if (!days.includes(dayIndex)) continue;

      const sMin = parseTimeToMinutes(w.start || w.from || w.open || w.begin);
      const eMin = parseTimeToMinutes(w.end || w.to || w.close || w.finish);
      if (sMin == null || eMin == null) {
        return true;
      }
      if (sMin <= startMin && endMin <= eMin) return true;
    }
    return false;
  };

  const windows = [];
  if (Array.isArray(employee.availability) && employee.availability.length) employee.availability.forEach(a => windows.push(a));
  if (employee.workingHours && typeof employee.workingHours === 'object') {
    const weekMap = { mon:1, tue:2, wed:3, thu:4, fri:5, sat:6, sun:0 };
    for (const k of Object.keys(employee.workingHours)) {
      const val = employee.workingHours[k];
      const day = (weekMap[k.slice(0,3).toLowerCase()] !== undefined) ? weekMap[k.slice(0,3).toLowerCase()] : (Number(k) || null);
      if (!val) continue;
      if (typeof val === 'string') {
        const parts = val.split(/[-–—]/).map(s => s.trim());
        windows.push({ day, start: parts[0], end: parts[1] });
      } else if (typeof val === 'object' && (val.start || val.end)) {
        windows.push({ day, start: val.start, end: val.end });
      }
    }
  }
  if (Array.isArray(employee.weeklyAvailability) && employee.weeklyAvailability.length) employee.weeklyAvailability.forEach(a => windows.push(a));
  if (Array.isArray(employee.hours) && employee.hours.length) employee.hours.forEach(a => windows.push(a));

  if (windows.length > 0) {
    const okWindow = testWindows(windows);
    if (!okWindow) return { ok: false, reason: 'El trabajador no está disponible en el horario elegido según su horario.' };
  }

  if (bookingsArray && bookingsArray.length) {
    for (const other of bookingsArray) {
      if (!other) continue;
      const otherEmployeeId = other.employeeId || other.employee || other.employeeUID || other.employeeUserUid;
      if (!otherEmployeeId || !employee) continue;
      if (String(otherEmployeeId) !== String(employee.id || employee.uid || employee.userUid)) continue;
      if (other.id === bookingToExcludeId) continue;
      const otherStart = parseScheduled(other);
      if (!otherStart) continue;
      const otherDur = Number(other.duration || other.serviceDuration || other.durationMinutes || 60) || 60;
      const otherEnd = addMinutes(otherStart, otherDur);
      const reqStart = wallToInstant(datetime, bookingContext);
      const reqEnd = addMinutes(reqStart, durationMin || 60);
      if (rangesOverlap(reqStart.getTime(), reqEnd.getTime(), otherStart.getTime(), otherEnd.getTime())) {
        return { ok: false, reason: 'El trabajador tiene otra reserva en ese horario.' };
      }
    }
  }

  return { ok: true, reason: null };
}


function isSecureViewerBooking(row = {}, routeBusinessId = '') {
  if (!row || row.viewerOwned !== true) return false;
  if (row.isGuestBooking === true || row.hasUserAccount === false) return false;

  const rowBusinessId = String(
    row.businessId || row.businessID || row.companyId || ''
  ).trim();
  const requiredBusinessId = String(routeBusinessId || '').trim();

  if (requiredBusinessId && rowBusinessId !== requiredBusinessId) return false;
  return Boolean(String(row.id || row.bookingId || '').trim() && rowBusinessId);
}

function callableMessage(error, fallback) {
  const message = String(error?.message || '').trim();
  if (message && !message.toLowerCase().includes('firebaseerror')) return message;
  return fallback;
}

/* ---------------------------
   Component MyBookings
---------------------------*/
export default function MyBookings({ businessId, onClose, openSource }) {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const whatsappTarget = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return {
        bookingId: String(params.get("bookingId") || "").trim(),
        businessId: String(params.get("businessId") || "").trim(),
        source: String(params.get("source") || "").trim().toLowerCase(),
      };
    } catch (_) {
      return { bookingId: "", businessId: "", source: "" };
    }
  }, [location.search]);

  const whatsappTargetDomId = useMemo(() => {
    const clean = whatsappTarget.bookingId.replace(/[^A-Za-z0-9_-]/g, "_");
    return clean ? `loop-whatsapp-booking-${clean}` : "";
  }, [whatsappTarget.bookingId]);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [bookingViews, setBookingViews] = useState(() => ({
    active: emptyBookingViewState(),
    history: emptyBookingViewState(),
  }));
  const [activeTab, setActiveTab] = useState('active');
  const [recoverPaymentBooking, setRecoverPaymentBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [historyPeriod, setHistoryPeriod] = useState('all');
  const [historySort, setHistorySort] = useState('desc');

  const [lastError, setLastError] = useState(null);
  const [businessProfiles, setBusinessProfiles] = useState({});
  const [availabilityBookings, setAvailabilityBookings] = useState([]);

  const bookingLoadSequenceRef = useRef({ active: 0, history: 0 });
  const legacyBaseRef = useRef({});
  const bookingViewsRef = useRef(bookingViews);
  bookingViewsRef.current = bookingViews;
  const lastSuccessfulLoadRef = useRef({ active: null, history: null });
  const lastViewLoadAtRef = useRef({ active: 0, history: 0 });
  const lastFocusRefreshRef = useRef(0);
  const whatsappScrollKeyRef = useRef("");
  const whatsappAutoPageRef = useRef(0);

  // Modal state for "Cambiar Reserva"
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [newDate, setNewDate] = useState(''); // yyyy-mm-dd
  const [newHour, setNewHour] = useState('09'); // '00' .. '23'
  const [newMinute, setNewMinute] = useState('00'); // '00' or '30'
  const [savingChange, setSavingChange] = useState(false);

  // employee doc and availability validation state
  const [employeeDoc, setEmployeeDoc] = useState(null);
  const [availabilityCheck, setAvailabilityCheck] = useState({ ok: true, reason: null });

  useEffect(() => {
    let mounted = true;
    let pollTimer = null;
    let unsubAuth = null;

    const applyBookingsUser = (candidate = null) => {
      try {
        const effectiveUser = getEffectiveBookingsUser(candidate || getAuth().currentUser || null);
        if (!mounted) return;
        if (effectiveUser && (effectiveUser.uid || effectiveUser.email)) {
          setUser((current) => (
            bookingsUserIdentityKey(current) === bookingsUserIdentityKey(effectiveUser)
              ? current
              : effectiveUser
          ));
        } else {
          setUser(null);
        }
        setLoadingUser(false);
      } catch (e) {
        if (mounted) {
          setUser(null);
          setLoadingUser(false);
        }
      }
    };

    if (isIosCapacitor()) {
      applyBookingsUser(null);

      try {
        unsubAuth = getAuth().onAuthStateChanged((current) => applyBookingsUser(current));
      } catch (_) {}

      const events = [
        'manual-auth-changed',
        'manual-auth-detected',
        'loop-auth-login-success',
        'loop-auth-session-changed',
        'manual-auth-cleared',
        'loop-auth-cleared',
        'visibilitychange',
        'focus',
      ];
      const eventHandler = () => applyBookingsUser(null);
      events.forEach((eventName) => {
        try { window.addEventListener(eventName, eventHandler); } catch (_) {}
      });

      let attempts = 0;
      pollTimer = window.setInterval(() => {
        attempts += 1;
        applyBookingsUser(null);
        if (attempts >= 12) {
          try { window.clearInterval(pollTimer); } catch (_) {}
          pollTimer = null;
        }
      }, 700);

      return () => {
        mounted = false;
        try { if (unsubAuth) unsubAuth(); } catch (_) {}
        try { if (pollTimer) window.clearInterval(pollTimer); } catch (_) {}
        events.forEach((eventName) => {
          try { window.removeEventListener(eventName, eventHandler); } catch (_) {}
        });
      };
    }

    const auth = getAuth();
    const un = onAuthStateChanged(auth, (u) => {
      if (!mounted) return;
      setUser(u);
      setLoadingUser(false);
    });
    return () => {
      mounted = false;
      try { un(); } catch (_) {}
    };
  }, []);


  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(String(searchTerm || '').trim());
    }, 320);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const userIdentityKey = bookingsUserIdentityKey(user);
  const currentIdentityRef = useRef(userIdentityKey);
  currentIdentityRef.current = userIdentityKey;

  const loadBookingView = useCallback(async ({
    view = 'active',
    reset = true,
    cursor = null,
    reason = 'manual',
    showLoader = true,
  } = {}) => {
    const safeView = view === 'history' ? 'history' : 'active';
    const effectiveUser = getEffectiveBookingsUser(user);
    const identity = bookingsUserIdentityKey(effectiveUser);
    activateBookingMemory(identity);
    if(reason.startsWith('after-'))clearBookingMemory();

    if (!effectiveUser || !String(effectiveUser.uid || '').trim()) {
      setBookingViews({ active: emptyBookingViewState(), history: emptyBookingViewState() });
      setLastError({
        source: 'paged-callable',
        code: 'missing-user',
        message: 'No se pudo validar la sesión para consultar tus reservas.',
      });
      return [];
    }

    const nextSequence = Number(bookingLoadSequenceRef.current[safeView] || 0) + 1;
    bookingLoadSequenceRef.current[safeView] = nextSequence;

    setBookingViews((current) => {
      const currentView = current[safeView] || emptyBookingViewState();
      return {
        ...current,
        [safeView]: {
          ...currentView,
          loading: reset ? Boolean(showLoader) : currentView.loading,
          loadingMore: !reset,
          error: null,
        },
      };
    });

    setLastError(null);

    const requestStatusFilter = safeView === 'active'
      ? (['pending', 'confirmed'].includes(statusFilter) ? statusFilter : 'all')
      : (['completed', 'cancelled'].includes(statusFilter) ? statusFilter : 'all');

    const request = {
      parallelLegacy: true,
      view: safeView,
      pageSize: MYBOOKINGS_PAGE_SIZE,
      statusFilter: requestStatusFilter,
      search: String(debouncedSearchTerm || '').trim(),
      ...(safeView === 'history' ? {
        period: historyPeriod,
        sort: historySort,
      } : {}),
      ...(cursor ? { cursor } : {}),
    };
    const memoryKey=bookingMemoryKey(identity,businessId,request);
    const remembered=reset && reason==='view-query-change' ? readBookingMemory(identity,memoryKey) : null;
    if(remembered)setBookingViews(current=>({...current,[safeView]:{...remembered,loading:true,loadingMore:false,error:null}}));

    try {
      const response = await loadBookingPageOnce(bookingsUserIdentityKey(effectiveUser), request, () => callCallableNativeSafe({
        functionName: 'getUserBookingsPageV1',
        data: request,
        timeoutMs: 30000,
        requireAuth: true,
        requireAppCheck: false,
      }), {force:reason.startsWith('after-')});

      if (bookingLoadSequenceRef.current[safeView] !== nextSequence || currentIdentityRef.current !== bookingsUserIdentityKey(effectiveUser)) return [];

      const payload = response?.data || {};
      const secureRows = (Array.isArray(payload.bookings) ? payload.bookings : [])
        .filter((booking) => isSecureViewerBooking(booking, businessId));
      const pageInfo = payload.pageInfo && typeof payload.pageInfo === 'object'
        ? payload.pageInfo
        : {};
      if(pageInfo.legacyPending)legacyBaseRef.current[safeView]={key:memoryKey,items:reset?secureRows:mergeBookingRows(bookingViewsRef.current?.[safeView]?.items || [],secureRows)};

      setBookingViews((current) => {
        const previous = current[safeView] || emptyBookingViewState();
        const base=reason==='legacy-followup' && legacyBaseRef.current[safeView]?.key===memoryKey ? legacyBaseRef.current[safeView].items : previous.items;
        const items = (pageInfo.legacyPending && remembered ? remembered.items : reset ? secureRows : mergeBookingRows(base, secureRows)).slice().sort((a,b) => {
          const delta = (parseScheduled(a)?.getTime() || 0) - (parseScheduled(b)?.getTime() || 0);
          return safeView === 'history' && historySort === 'desc' ? -delta : delta;
        });
        const snapshot = {
            items,
            pageInfo: {
              pageSize: MYBOOKINGS_PAGE_SIZE,
              returned: Number(pageInfo.returned ?? secureRows.length),
              hasMore: pageInfo.hasMore === true,
              nextCursor: pageInfo.nextCursor || null,
              canonicalScanned: Number(pageInfo.canonicalScanned || 0),
              legacyScanned: Number(pageInfo.legacyScanned || 0),
              legacyFallback: pageInfo.legacyFallback === true,
              legacyPending: pageInfo.legacyPending === true,
              degraded: pageInfo.degraded === true,
            },
            loading: false,
            loadingMore: false,
            loaded: true,
            error: null,
          };
        writeBookingMemory(identity,memoryKey,snapshot);
        return {...current,[safeView]:snapshot};
      });

      const loadedAt = Date.now();
      lastViewLoadAtRef.current[safeView] = loadedAt;
      lastSuccessfulLoadRef.current[safeView] = {
        reason,
        at: new Date(loadedAt).toISOString(),
        transport: response?.transport || 'firebase-callable-sdk',
        count: secureRows.length,
        hasMore: pageInfo.hasMore === true,
        canonicalScanned: Number(pageInfo.canonicalScanned || 0),
        legacyScanned: Number(pageInfo.legacyScanned || 0),
        legacyFallback: pageInfo.legacyFallback === true,
      };

      if (pageInfo.legacyPending && pageInfo.nextCursor) {
        return loadBookingView({view:safeView,reset:false,cursor:pageInfo.nextCursor,reason:'legacy-followup',showLoader:false});
      }
      return secureRows;
    } catch (error) {
      if (bookingLoadSequenceRef.current[safeView] === nextSequence && currentIdentityRef.current === bookingsUserIdentityKey(effectiveUser)) {
        const normalizedError = {
          source: 'paged-callable',
          code: error?.code || 'unknown',
          message: callableMessage(error, 'No pudimos cargar tus reservas en este momento.'),
        };
        setLastError(normalizedError);
        setBookingViews((current) => ({
          ...current,
          [safeView]: {
            ...(current[safeView] || emptyBookingViewState()),
            loading: false,
            loadingMore: false,
            error: normalizedError,
          },
        }));
      }
      throw error;
    }
  }, [businessId, debouncedSearchTerm, historyPeriod, historySort, statusFilter, user]);

  useEffect(() => {
    if (!userIdentityKey) {
      if (!loadingUser) activateBookingMemory('');
      setBookingViews({ active: emptyBookingViewState(), history: emptyBookingViewState() });
      setBusinessProfiles({});
      return undefined;
    }

    loadBookingView({
      view: activeTab,
      reset: true,
      reason: 'view-query-change',
      showLoader: true,
    }).catch((error) => {
      console.error('[MyBookings] paged view load failed', error);
    });

    return undefined;
  }, [activeTab, debouncedSearchTerm, historyPeriod, historySort, loadBookingView, statusFilter, userIdentityKey, loadingUser]);

  useEffect(() => {
    if (!userIdentityKey) return undefined;

    const refresh = () => {
      try {
        if (document.visibilityState === 'hidden') return;
      } catch (_) {}

      const now = Date.now();
      if (now - Number(lastFocusRefreshRef.current || 0) < 45000) return;
      if (now - Number(lastViewLoadAtRef.current[activeTab] || 0) < 45000) return;

      const state = bookingViews[activeTab] || emptyBookingViewState();
      if (state.loading || state.loadingMore) return;

      lastFocusRefreshRef.current = now;
      loadBookingView({
        view: activeTab,
        reset: true,
        reason: 'focus-refresh-throttled',
        showLoader: false,
      }).catch((error) => {
        console.warn('[MyBookings] throttled focus refresh failed', error);
      });
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [activeTab, bookingViews, loadBookingView, userIdentityKey]);

  const activeBookings = bookingViews.active.items || [];
  const historyBookings = bookingViews.history.items || [];
  const currentViewState = bookingViews[activeTab] || emptyBookingViewState();
  const loadingBookings = currentViewState.loading || (!currentViewState.items.length && currentViewState.loadingMore);

  const bookingsMap = useMemo(() => {
    const map = new Map();
    [...activeBookings, ...historyBookings].forEach((booking) => {
      if (!isSecureViewerBooking(booking, businessId)) return;
      const key = bookingRowKey(booking);
      if (key) map.set(key, booking);
    });
    return map;
  }, [activeBookings, historyBookings, businessId]);

  const myBookings = useMemo(() => Array.from(bookingsMap.values()), [bookingsMap]);

  const whatsappTargetBooking = useMemo(() => {
    if (!whatsappTarget.bookingId) return null;
    return myBookings.find((booking) => {
      const bookingId = String(booking.id || booking.bookingId || '').trim();
      const bookingBusinessId = String(booking.businessId || booking.businessID || '').trim();
      if (bookingId !== whatsappTarget.bookingId) return false;
      if (whatsappTarget.businessId && bookingBusinessId !== whatsappTarget.businessId) return false;
      return true;
    }) || null;
  }, [myBookings, whatsappTarget.bookingId, whatsappTarget.businessId]);

  useEffect(() => {
    if (whatsappTarget.source !== 'whatsapp' || !whatsappTarget.bookingId || !userIdentityKey) return;
    if (whatsappTargetBooking) return;

    if (bookingViews.active.loaded && !bookingViews.history.loaded && activeTab !== 'history') {
      setStatusFilter('all');
      setSearchTerm('');
      setHistoryPeriod('all');
      setHistorySort('desc');
      setActiveTab('history');
      return;
    }

    if (
      activeTab === 'history' &&
      bookingViews.history.loaded &&
      bookingViews.history.pageInfo?.hasMore &&
      bookingViews.history.pageInfo?.nextCursor &&
      !bookingViews.history.loadingMore &&
      whatsappAutoPageRef.current < 8
    ) {
      whatsappAutoPageRef.current += 1;
      loadBookingView({
        view: 'history',
        reset: false,
        cursor: bookingViews.history.pageInfo.nextCursor,
        reason: 'whatsapp-target-pagination',
        showLoader: false,
      }).catch((error) => {
        console.warn('[MyBookings] WhatsApp target pagination failed', error);
      });
    }
  }, [
    activeTab,
    bookingViews.active.loaded,
    bookingViews.history.loaded,
    bookingViews.history.loadingMore,
    bookingViews.history.pageInfo,
    loadBookingView,
    userIdentityKey,
    whatsappTarget.bookingId,
    whatsappTarget.source,
    whatsappTargetBooking,
  ]);

  useEffect(() => {
    if (!whatsappTargetBooking || !whatsappTargetDomId) return;

    const targetStatus = String(whatsappTargetBooking.status || 'pending').toLowerCase();
    const targetDate = parseScheduled(whatsappTargetBooking);
    const belongsToHistory =
      ['cancelled', 'cancelada', 'canceled'].includes(targetStatus) ||
      Boolean(targetDate && targetDate < new Date());

    setActiveTab(belongsToHistory ? 'history' : 'active');
    setStatusFilter('all');
    setSearchTerm('');

    const scrollKey = `${whatsappTarget.businessId}:${whatsappTarget.bookingId}:${belongsToHistory ? 'history' : 'active'}`;
    if (whatsappScrollKeyRef.current === scrollKey) return;
    whatsappScrollKeyRef.current = scrollKey;

    const timer = window.setTimeout(() => {
      try {
        const target = document.getElementById(whatsappTargetDomId);
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (_) {}
    }, 180);

    return () => window.clearTimeout(timer);
  }, [whatsappTargetBooking, whatsappTargetDomId, whatsappTarget.bookingId, whatsappTarget.businessId]);

  useEffect(() => {
    try {
      window.__LOOP_MYBOOKINGS_DEBUG__ = () => ({
        isIosCapacitor: isIosCapacitor(),
        stateUserUid: user?.uid || '',
        activeTab,
        statusFilter,
        search: debouncedSearchTerm,
        historyPeriod,
        historySort,
        active: {
          count: activeBookings.length,
          loaded: bookingViews.active.loaded,
          loading: bookingViews.active.loading,
          hasMore: bookingViews.active.pageInfo?.hasMore === true,
          pageInfo: bookingViews.active.pageInfo,
          lastSuccessfulLoad: lastSuccessfulLoadRef.current.active,
        },
        history: {
          count: historyBookings.length,
          loaded: bookingViews.history.loaded,
          loading: bookingViews.history.loading,
          hasMore: bookingViews.history.pageInfo?.hasMore === true,
          pageInfo: bookingViews.history.pageInfo,
          lastSuccessfulLoad: lastSuccessfulLoadRef.current.history,
        },
        lastError,
      });
    } catch (_) {}
  }, [
    activeBookings.length,
    activeTab,
    bookingViews.active,
    bookingViews.history,
    debouncedSearchTerm,
    historyBookings.length,
    historyPeriod,
    historySort,
    lastError,
    statusFilter,
    user,
  ]);

  const loadAvailabilityContext = useCallback(async () => {
    try {
      const response = await callCallableNativeSafe({
        functionName: 'getUserBookings',
        data: { limit: 500 },
        timeoutMs: 45000,
        requireAuth: true,
        requireAppCheck: false,
      });
      const rows = (Array.isArray(response?.data?.bookings) ? response.data.bookings : [])
        .filter((booking) => isSecureViewerBooking(booking, businessId));
      setAvailabilityBookings(rows);
      return rows;
    } catch (error) {
      console.warn('[MyBookings] lazy availability context failed; using loaded page only', error);
      return Array.from(bookingsMap.values());
    }
  }, [bookingsMap, businessId]);

  async function handleCancel(booking) {
    if (!booking || !booking.id) return;
    const sched = parseScheduled(booking);
    if (sched && sched < new Date()) return alert(t('No se puede cancelar una reserva pasada.'));
    if (!window.confirm(`Confirmás cancelar la reserva de "${booking.serviceName || 'servicio'}" el ${sched ? sched.toLocaleString() : ''}?`)) return;

    const resolvedBusinessId = String(booking.businessId || businessId || '').trim();
    if (!resolvedBusinessId) {
      window.alert(t('No se pudo identificar el negocio de esta reserva.'));
      return;
    }

    try {
      await callCallableNativeSafe({
        functionName: 'cancelUserBooking',
        data: {
          businessId: resolvedBusinessId,
          bookingId: String(booking.id),
        },
        timeoutMs: 45000,
        requireAuth: true,
        requireAppCheck: false,
      });

      await loadBookingView({
        view: 'active',
        reset: true,
        reason: 'after-cancel',
        showLoader: false,
      });

      if (bookingViews.history.loaded) {
        loadBookingView({
          view: 'history',
          reset: true,
          reason: 'after-cancel-history-sync',
          showLoader: false,
        }).catch(() => {});
      }

      window.alert(t('Reserva cancelada correctamente.'));
    } catch (error) {
      console.error('cancelUserBooking failed', error);
      const message = callableMessage(error, 'No pudimos cancelar esta reserva.');
      setLastError({ source: 'cancelUserBooking', code: error?.code || 'unknown', message });
      window.alert(t(message));
    }
  }

  // Round to nearest 30 minutes for initial prefill
  function roundToNearest30(d) {
    if (!(d instanceof Date)) return d;
    const mins = d.getMinutes();
    if (mins < 15) { d.setMinutes(0); }
    else if (mins < 45) { d.setMinutes(30); }
    else { d.setHours(d.getHours() + 1); d.setMinutes(0); }
    d.setSeconds(0); d.setMilliseconds(0);
    return d;
  }

  // --- New: Change (reschedule) modal handlers ---
  const openChangeModal = (booking) => {
    const dt = parseScheduled(booking) || new Date();
    const rounded = roundToNearest30(wallDate(dt, booking));
    const yyyy = rounded.getFullYear();
    const mm = String(rounded.getMonth() + 1).padStart(2, "0");
    const dd = String(rounded.getDate()).padStart(2, "0");
    const hh = String(rounded.getHours()).padStart(2, "0");
    const mi = String(rounded.getMinutes()).padStart(2, "0");
    setNewDate(`${yyyy}-${mm}-${dd}`);
    setNewHour(hh);
    setNewMinute(mi === '00' ? '00' : '30'); // normalize
    setEditingBooking(booking);
    setEmployeeDoc(null);
    setAvailabilityBookings(Array.from(bookingsMap.values()));
    setAvailabilityCheck({ ok: true, reason: null });
    setChangeModalOpen(true);
    loadAvailabilityContext().catch(() => {});
  };

  // When editingBooking changes, fetch employee doc (if any) to validate availability
  useEffect(() => {
    let mounted = true;
    async function fetchEmployee() {
      setEmployeeDoc(null);
      setAvailabilityCheck({ ok: true, reason: null });
      if (!editingBooking) return;
      const empId = editingBooking.employeeId || editingBooking.employee || editingBooking.employeeUserUid || null;
      const bId = editingBooking.businessId || businessId || null;
      if (!empId || !bId) return;
      try {
        let emp = null;
        if (isIosCapacitor()) {
          emp = await fsClient.getDocByPath(`businesses/${bId}/employees/${String(empId)}`, { db });
        } else {
          const empRef = doc(db, 'businesses', bId, 'employees', String(empId));
          const snap = await getDoc(empRef);
          if (snap && snap.exists()) emp = { id: snap.id, ...snap.data() };
        }
        if (mounted && emp) {
          setEmployeeDoc(emp);
          const dt = (() => {
            if (!newDate || newHour == null || newMinute == null) return parseScheduled(editingBooking) || new Date();
            const yy = Number(newDate.split('-')[0]), mmn = Number(newDate.split('-')[1]), dd = Number(newDate.split('-')[2]);
            return new Date(yy, mmn - 1, dd, Number(newHour), Number(newMinute), 0, 0);
          })();
          const dur = Number(editingBooking.duration || editingBooking.serviceDuration || editingBooking.durationMinutes || 60) || 60;
          const bookingsArr = availabilityBookings.length ? availabilityBookings : Array.from(bookingsMap.values());
          const chk = isEmployeeAvailableAt(emp, dt, dur, bookingsArr, editingBooking.id, editingBooking);
          setAvailabilityCheck(chk);
        } else {
          setEmployeeDoc(null);
          setAvailabilityCheck({ ok: true, reason: null });
        }
      } catch (e) {
        console.error('fetchEmployee error', e);
        setEmployeeDoc(null);
        setAvailabilityCheck({ ok: true, reason: null });
      }
    }
    fetchEmployee();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBooking]);

  // Re-validate when newDate/newHour/newMinute changes
  useEffect(() => {
    if (!editingBooking) return;
    const dtValid = newDate && newHour != null && newMinute != null;
    if (!dtValid) { setAvailabilityCheck({ ok: true, reason: null }); return; }
    const [y,m,d] = newDate.split('-').map(Number);
    const hh = Number(newHour), mm = Number(newMinute);
    if ([y,m,d,hh,mm].some(v => Number.isNaN(v))) { setAvailabilityCheck({ ok: false, reason: 'Fecha u hora inválida' }); return; }

    // Enforce minutes to be 00 or 30 (select already enforces, pero doble-check)
    if (mm % 30 !== 0) {
      setAvailabilityCheck({ ok: false, reason: 'Selecciona un horario en intervalos de 30 minutos (ej. 09:00 o 09:30).' });
      return;
    }

    const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
    const now = new Date();

    // Prevent selecting past times
    let requestedInstant;
    try { requestedInstant = wallToInstant(dt, editingBooking); } catch (error) { setAvailabilityCheck({ok:false,reason:error.message}); return; }
    if (requestedInstant.getTime() <= now.getTime()) {
      setAvailabilityCheck({ ok: false, reason: 'No puedes seleccionar una fecha u hora pasada.' });
      return;
    }

    const dur = Number(editingBooking.duration || editingBooking.serviceDuration || editingBooking.durationMinutes || 60) || 60;
    const bookingsArr = availabilityBookings.length ? availabilityBookings : Array.from(bookingsMap.values());
    const chk = isEmployeeAvailableAt(employeeDoc, dt, dur, bookingsArr, editingBooking.id, editingBooking);
    setAvailabilityCheck(chk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDate, newHour, newMinute, employeeDoc, bookingsMap, availabilityBookings]);

  const submitChange = async () => {
    if (!editingBooking) return;
    if (!newDate || newHour == null || newMinute == null) { alert(t('Selecciona fecha y hora válidas')); return; }
    const [y, m, d] = newDate.split('-').map(Number);
    const hh = Number(newHour), mm = Number(newMinute);
    if ([y, m, d, hh, mm].some((value) => Number.isNaN(value))) { alert(t('Fecha u hora inválida')); return; }

    let newScheduled;
    try { newScheduled = localInstant(newDate, `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`, businessZone(editingBooking)); } catch (error) { alert(t(error.message)); return; }
    if (newScheduled.getTime() <= Date.now()) {
      alert(t('No puedes elegir una fecha/hora pasada.'));
      return;
    }

    if (!availabilityCheck.ok) {
      alert(t(availabilityCheck.reason || 'El trabajador no está disponible en ese horario.'));
      return;
    }

    const resolvedBusinessId = String(editingBooking.businessId || businessId || '').trim();
    if (!resolvedBusinessId) {
      alert(t('No se pudo identificar el negocio de esta reserva.'));
      return;
    }

    setSavingChange(true);
    try {
      await callCallableNativeSafe({
        functionName: 'requestUserBookingChange',
        data: {
          businessId: resolvedBusinessId,
          bookingId: String(editingBooking.id),
          date: newDate,
          time: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
        },
        timeoutMs: 45000,
        requireAuth: true,
        requireAppCheck: false,
      });

      await loadBookingView({
        view: 'active',
        reset: true,
        reason: 'after-change-request',
        showLoader: false,
      });

      alert(t('Solicitud de cambio enviada. La empresa debe aprobar la nueva fecha.'));
      setChangeModalOpen(false);
      setEditingBooking(null);
    } catch (error) {
      console.error('requestUserBookingChange failed', error);
      const message = callableMessage(error, 'No se pudo enviar la solicitud de cambio.');
      setLastError({ source: 'requestUserBookingChange', code: error?.code || 'unknown', message });
      alert(t(message));
    } finally {
      setSavingChange(false);
    }
  };



  // Prepare hour options (00..23) and minute options (00,30)
  const hourOptions = Array.from({ length: 24 }).map((_, i) => String(i).padStart(2, '0'));
  const minuteOptions = ['00', '30'];

  const filterOptions = activeTab === 'active'
    ? [
        { key: 'all', label: 'Todas' },
        { key: 'pending', label: 'Pendientes' },
        { key: 'confirmed', label: 'Confirmadas' },
      ]
    : [
        { key: 'all', label: 'Todas' },
        { key: 'completed', label: 'Realizadas' },
        { key: 'cancelled', label: 'Canceladas' },
      ];

  const visibleBookings = activeTab === 'active' ? activeBookings : historyBookings;
  const hasActiveFilters = Boolean(
    statusFilter !== 'all' ||
    String(searchTerm || '').trim() ||
    (activeTab === 'history' && (historyPeriod !== 'all' || historySort !== 'desc'))
  );

  useEffect(() => {
    let cancelled = false;

    async function loadBusinessProfilesForVisibleBookings() {
      try {
        const ids = Array.from(new Set((visibleBookings || [])
          .map((booking) => String(booking.businessId || booking.businessID || booking.companyId || '').trim())
          .filter(Boolean)));

        if (!ids.length) return;

        const missing = ids.filter((id) => !businessProfiles[id]);
        if (!missing.length) return;

        const loadedEntries = await Promise.allSettled(missing.slice(0, MYBOOKINGS_PAGE_SIZE).map(async (id) => {
          const snap = await getDoc(doc(db, 'businesses', id));
          if (!snap.exists()) return [id, {}];
          return [id, { id, ...(snap.data() || {}) }];
        }));

        if (cancelled) return;

        const next = {};
        loadedEntries.forEach((result) => {
          if (result.status !== 'fulfilled') return;
          const [id, data] = result.value || [];
          if (id) next[id] = data || {};
        });

        if (Object.keys(next).length) {
          setBusinessProfiles((current) => ({ ...current, ...next }));
        }
      } catch (err) {
        console.warn('[MyBookings] visible business profile preload failed', err);
      }
    }

    loadBusinessProfilesForVisibleBookings();

    return () => {
      cancelled = true;
    };
  }, [visibleBookings, businessProfiles]);

  // Keep every MyBookings hook above conditional rendering. React requires
  // identical hook order while Firebase identity transitions loading -> user/null.
  if (loadingUser) return <div className="mybookings-root"><div className="mb-loading">{t("Cargando usuario...")}</div></div>;
  if (!user) return <div className="mybookings-root"><div className="mb-empty">{t("Debes iniciar sesión para ver tus reservas.")}</div></div>;

  function clearBookingFilters() {
    setStatusFilter('all');
    setSearchTerm('');
    if (activeTab === 'history') {
      setHistoryPeriod('all');
      setHistorySort('desc');
    }
  }


  function BookingCard({ booking, mode }) {
    const sched = parseScheduled(booking);
    const key = booking._path || `${booking.businessId || 'nb'}/${booking.id || Math.random()}`;
    const durationVal = Number(booking.duration || booking.serviceDuration || booking.durationMinutes || 60) || 60;
    const scheduleParts = formatBookingDateParts(sched, booking);
    const status = (booking.status || '').toLowerCase();
    const canCancel = mode === 'active' && status !== 'cancelled';
    const membershipSummary = myBookingMembershipSummary(booking);
    const paymentSummary = booking.paymentSummary;
    const eventProgress = eventBookingProgress(booking);
    const paymentMoney = (cents) => Number.isSafeInteger(cents) && cents >= 0
      ? new Intl.NumberFormat(interfaceLocale(), {style: 'currency', currency: paymentSummary?.currency || booking.currency || 'USD'}).format(cents / 100)
      : 'Sin información';
    const businessProfile = booking.businessId ? (businessProfiles[String(booking.businessId)] || {}) : {};
    const businessName = mbCleanText(mbBusinessDisplayName(booking, businessProfile)) || "Negocio";
    const businessLogo = mbPickLogoFrom(booking) || mbPickLogoFrom(businessProfile);
    const businessArea = mbBusinessArea(booking, businessProfile);
    const serviceName = mbCleanText(mbServiceDisplayName(booking)) || "Servicio";
    const bookingCardId = String(booking.id || booking.bookingId || "").trim();
    const bookingCardBusinessId = String(booking.businessId || booking.businessID || "").trim();
    const isWhatsappTarget = Boolean(
      whatsappTarget.bookingId &&
      bookingCardId === whatsappTarget.bookingId &&
      (!whatsappTarget.businessId || bookingCardBusinessId === whatsappTarget.businessId)
    );

    // Pagos online / Wompi: oculto temporalmente hasta conectar producción.
    // La reserva mantiene sus datos de precio/status en Firestore, pero el cliente no ve CTA de pago.
    return (
      <article
        key={key}
        id={isWhatsappTarget ? whatsappTargetDomId : undefined}
        className={`mb-booking-card${isWhatsappTarget ? " is-whatsapp-target" : ""}`}
        style={isWhatsappTarget ? {
          border: "2px solid #14b8a6",
          boxShadow: "0 0 0 5px rgba(20,184,166,.13), 0 18px 44px rgba(15,23,42,.12)",
          scrollMarginTop: 120,
        } : undefined}
      >
        <div className="mb-card-top">
          <div className="mb-business-media-mark" aria-hidden="true">
            {businessLogo ? (
              <img src={businessLogo} alt="" loading="lazy" />
            ) : (
              <span>{mbFirstLetter(businessName)}</span>
            )}
          </div>

          <div className="mb-card-main">
            <div className="mb-card-title-row">
              <div className="mb-card-title-stack">
                <h3 className="mb-business-title">{businessName}</h3>
                <p className="mb-service-subtitle">{serviceName}</p>
                {businessArea ? <p className="mb-business-area">{businessArea}</p> : null}
              </div>
              <StatusBadge status={booking.status} />
            </div>

            <div className="mb-card-schedule">
              <div className="mb-card-schedule-date">
                <span>{t("Fecha")}</span>
                <strong>{scheduleParts.dateLabel}</strong>
              </div>
              <div className="mb-card-schedule-time">
                <span>{t("Hora")}</span>
                <strong>{scheduleParts.timeLabel}</strong>
              </div>
            </div>

            <div className="mb-card-detail-row">
              <div className="mb-card-professional">
                <span>{t("Profesional")}</span>
                <strong>{booking.employeeName || booking.employeeId || 'Sin asignar'}</strong>
              </div>
              <span className="mb-duration-pill">{durationVal} min</span>
            </div>
          </div>
        </div>


        {paymentSummary && !membershipSummary && paymentSummary.totalCents !== null ? (
          <section aria-label={t("Resumen del pago")} style={{marginTop: 12, padding: 14, borderRadius: 14, background: '#f0fdfa', border: '1px solid #ccfbf1'}}>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 20}}>
              <div><span>{paymentSummary.estimated ? t("Total estimado") : 'Total'}</span><br /><strong>{paymentMoney(paymentSummary.totalCents)}</strong></div>
              <div><span>{t("Pagado")}</span><br /><strong>{paymentMoney(paymentSummary.paidCents)}</strong></div>
              <div><span>{paymentSummary.estimated ? 'Saldo estimado' : t("Pendiente")}</span><br /><strong>{paymentMoney(paymentSummary.balanceCents)}</strong></div>
            </div>
            {paymentSummary.sandbox ? <small>{t("Pago de prueba · sin cobro real")}</small> : null}
          </section>
        ) : null}
        {membershipSummary ? (
          <div className="mb-membership-card" style={{ marginTop: 12, border: "1px solid #99f6e4", background: membershipSummary.processed ? "#ecfdf5" : "linear-gradient(135deg,#f0fdfa,#fff)", borderRadius: 20, padding: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <span style={{ display: "block", fontSize: 10, fontWeight: 950, letterSpacing: ".10em", textTransform: "uppercase", color: "#00796f" }}>{t("Membresía")}</span>
                <strong style={{ display: "block", marginTop: 3, color: "#0f172a", fontSize: 14, fontWeight: 950 }}>{membershipSummary.title}</strong>
                <small style={{ display: "block", marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 800, lineHeight: 1.35 }}>{membershipSummary.detail}</small>
              </div>
              <em style={{ fontStyle: "normal", borderRadius: 999, background: membershipSummary.processed ? "#bbf7d0" : "#fffbeb", color: membershipSummary.processed ? "#047857" : "#92400e", padding: "6px 9px", fontSize: 11, fontWeight: 950, whiteSpace: "nowrap" }}>{membershipSummary.processed ? "Procesada" : t("Pendiente")}</em>
            </div>
            <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, fontWeight: 750, lineHeight: 1.4 }}>{membershipSummary.note}</p>
          </div>
        ) : null}



        {booking.eventQuote ? <EventQuoteCard key={`${booking.id}-${booking.eventQuote.version}`} booking={booking} onUpdated={()=>loadBookingView({view:'active',reason:'after-quote',showLoader:false})}/> : null}
        {booking.eventPriceChange ? <EventPriceChangeCard key={`price-${booking.id}-${booking.eventPriceChange.version}-${booking.eventPriceChange.status}`} booking={booking} onUpdated={()=>loadBookingView({view:'active',reason:'after-price-change',showLoader:false})}/> : null}
        {eventProgress && (!booking.eventQuote || booking.eventQuote.status==='accepted') ? <section className="mb-event-progress" aria-label={t("Estado de tu evento")}>
          <strong>{eventProgress.label}</strong>
          <p>{eventProgress.message}</p>
          {eventProgress.amountCents != null ? <p>{t("Abono requerido:") + " "}<strong>{paymentMoney(eventProgress.amountCents)}</strong></p> : null}
          {eventProgress.deadlineMs ? <small>{t("Plazo:") + " "}{new Date(eventProgress.deadlineMs).toLocaleString(interfaceLocale())}</small> : null}
        </section> : null}
        <div className="mb-card-actions">
          {booking.eventBookingFlowVersion==='event_booking_approval_first_v1' && booking.eventApprovalStatus==='approved' && booking.eventBalanceOnlineEnabled!==false && !booking.paymentReviewRequired && ['confirmed','in_progress','completed'].includes(normalizeString(booking.status || booking.confirmationStatus)) && paymentSummary?.balanceCents>0 ? <button type="button" className="mb-primary-action" onClick={()=>setRecoverPaymentBooking(booking)}>{t("Pagar saldo ·") + " "}{paymentMoney(paymentSummary.balanceCents)}</button> : null}
          {normalizeString(booking.status || booking.confirmationStatus) === 'pending_payment' && (!eventProgress || eventProgress.canPay) ? <button type="button" className="mb-primary-action" onClick={() => setRecoverPaymentBooking(booking)}>{t("Consultar pago")}</button> : null}
          {mode === 'active' ? (
            <>
              <button type="button" onClick={() => openChangeModal(booking)} className="mb-primary-action">
                <Repeat className="w-4 h-4" />{t("Cambiar reserva")}</button>

              {canCancel ? (
                <button type="button" onClick={() => handleCancel(booking)} className="mb-danger-action">
                  <X className="w-4 h-4" />{t("Cancelar")}</button>
              ) : null}
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (booking.businessId) {
                  const params = new URLSearchParams();
                  params.set('businessId', booking.businessId);
                  if (booking.serviceId) params.set('serviceId', booking.serviceId);
                  if (booking.businessName) params.set('businessName', booking.businessName);
                  if (booking.date) params.set('date', booking.date);
                  if (booking.time) params.set('time', booking.time);
                  params.set('reschedule', '1');
                  navigate(`/booking?${params.toString()}`);
                } else {
                  window.alert(t('Reservar de nuevo no disponible para esta reserva (ruta desconocida).'));
                }
              }}
              className="mb-secondary-action"
            >
              <Repeat className="w-4 h-4" />{t("Reservar de nuevo")}</button>
          )}
        </div>


      </article>
    );
  }

  return (
    <div className="mybookings-root">
      {recoverPaymentBooking ? <BookingPaymentRecovery booking={recoverPaymentBooking} onClose={() => setRecoverPaymentBooking(null)} /> : null}
      <section className="mb-shell">
        <header className="mb-hero">
          <div>
            <div className="mb-eyebrow">{t("Reservas")}</div>
            <h2>{t("Mis Reservas")}</h2>
            <p>{t("Consulta tus próximas reservas, historial y cambios pendientes.")}</p>
          </div>

          {onClose ? (
            <button type="button" onClick={onClose} className="mb-close-pro">{t("Cerrar")}</button>
          ) : null}
        </header>

        <div className="mb-tabs-card">
          <button
            type="button"
            onClick={() => {
              whatsappAutoPageRef.current = 0;
              setActiveTab('active');
              setStatusFilter('all');
            }}
            className={`mb-tab ${activeTab === 'active' ? 'is-active' : ''}`}
          >
            <span>{t("Activas")}</span>
            <strong>{bookingCountLabel(bookingViews.active)}</strong>
          </button>

          <button
            type="button"
            onClick={() => {
              whatsappAutoPageRef.current = 0;
              setActiveTab('history');
              setStatusFilter('all');
            }}
            className={`mb-tab ${activeTab === 'history' ? 'is-active' : ''}`}
          >
            <span>{t("Historial")}</span>
            <strong>{bookingCountLabel(bookingViews.history)}</strong>
          </button>
        </div>

        <div className="mb-filter-panel mb-filter-panel-v3">
          <div className="mb-search-wrap">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("Buscar servicio, negocio o profesional")}
              aria-label={t("Buscar reservas")}
            />
            {searchTerm ? (
              <button type="button" className="mb-search-clear" onClick={() => setSearchTerm('')} aria-label={t("Limpiar búsqueda")}>
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>

          <div className="mb-filter-toolbar">
            <div className="mb-filter-chips" aria-label={t("Filtros de reserva")}>
              {filterOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setStatusFilter(option.key)}
                  className={`mb-filter-chip ${statusFilter === option.key ? 'is-active' : ''}`}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>

            {activeTab === 'history' ? (
              <div className="mb-history-controls">
                <label className="mb-filter-select-wrap">
                  <span>Período</span>
                  <select value={historyPeriod} onChange={(e) => setHistoryPeriod(e.target.value)} aria-label={t("Período del historial")}>
                    <option value="30d">{t("30 días")}</option>
                    <option value="3m">{t("3 meses")}</option>
                    <option value="1y">{t("1 año")}</option>
                    <option value="all">{t("Todo")}</option>
                  </select>
                </label>

                <label className="mb-filter-select-wrap">
                  <span>{t("Orden")}</span>
                  <select value={historySort} onChange={(e) => setHistorySort(e.target.value)} aria-label={t("Orden del historial")}>
                    <option value="desc">{t("Más recientes")}</option>
                    <option value="asc">{t("Más antiguas")}</option>
                  </select>
                </label>
              </div>
            ) : null}
          </div>

          {activeTab !== 'history' || hasActiveFilters || (currentViewState.loading && currentViewState.loaded) ? (
            <div className="mb-results-meta">
              <span>
                {currentViewState.loaded && !(loadingBookings && !visibleBookings.length)
                  ? t(activeTab === 'active' ? '{count} activas cargadas' : '{count} resultados cargados',{count:String(visibleBookings.length)+(currentViewState.pageInfo?.hasMore ? '+' : '')})
                  : t('Buscando tus reservas…')}
              </span>
              {currentViewState.loading && currentViewState.loaded ? <em>{t("Actualizando…")}</em> : null}
              {hasActiveFilters ? (
                <button type="button" onClick={clearBookingFilters}>{t("Limpiar filtros")}</button>
              ) : null}
            </div>
          ) : null}
        </div>

        {lastError ? (
          <div className="mb-soft-warning">
            {t(lastError.message || 'No pudimos actualizar tus reservas. Puedes reintentar sin salir de esta pantalla.')}
          </div>
        ) : null}

        {whatsappTarget.source === "whatsapp" && whatsappTarget.bookingId && !loadingBookings && !currentViewState.loadingMore && !currentViewState.pageInfo?.hasMore ? (
          whatsappTargetBooking ? (
            <div className="mb-soft-warning" role="status">{t("Abrimos la reserva vinculada desde WhatsApp y la resaltamos abajo.")}</div>
          ) : (
            <div className="mb-soft-warning" role="status">{t("No encontramos esa reserva dentro de esta cuenta. Verifica que hayas iniciado sesión con la cuenta usada al reservar.")}</div>
          )
        ) : null}

        <main className="mb-content-pro">
          {loadingBookings && visibleBookings.length === 0 ? (
            <div className="mb-loading-state">
              <div className="mb-skeleton-list">
                <div className="mb-skeleton-card" />
                <div className="mb-skeleton-card" />
              </div>
            </div>
          ) : visibleBookings.length === 0 ? (
            <div className="mb-empty-pro">
              <div className="mb-empty-icon">
                <Repeat className="w-5 h-5" />
              </div>
              <h3>
                {hasActiveFilters
                  ? 'No encontramos reservas con esos filtros'
                  : activeTab === 'active'
                    ? 'No tienes reservas activas'
                    : 'No tienes historial todavía'}
              </h3>
              <p>
                {hasActiveFilters
                  ? 'Prueba otro estado, período o limpia la búsqueda.'
                  : activeTab === 'active'
                    ? 'Cuando reserves un servicio, aparecerá aquí con todos los detalles.'
                    : 'Tus reservas realizadas o canceladas aparecerán en esta sección.'}
              </p>
              <div className="mb-empty-actions">
                {hasActiveFilters ? (
                  <button type="button" className="mb-secondary-action" onClick={clearBookingFilters}>{t("Limpiar filtros")}</button>
                ) : null}
                <button
                  type="button"
                  className="mb-secondary-action"
                  onClick={() => {
                    loadBookingView({
                      view: activeTab,
                      reset: true,
                      reason: 'manual-retry',
                      showLoader: true,
                    }).catch((error) => {
                      console.error('[MyBookings] manual retry failed', error);
                    });
                  }}
                >{t("Reintentar carga")}</button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-booking-grid">
                {visibleBookings.map((booking) => (
                  <BookingCard
                    key={bookingRowKey(booking) || `${booking.businessId || 'nb'}/${booking.id || Math.random()}`}
                    booking={booking}
                    mode={activeTab}
                  />
                ))}
              </div>

              {currentViewState.pageInfo?.hasMore && currentViewState.pageInfo?.nextCursor ? (
                <div className="mb-load-more-wrap">
                  <button
                    type="button"
                    className="mb-load-more"
                    disabled={currentViewState.loadingMore}
                    onClick={() => {
                      loadBookingView({
                        view: activeTab,
                        reset: false,
                        cursor: currentViewState.pageInfo.nextCursor,
                        reason: 'load-more',
                        showLoader: false,
                      }).catch((error) => {
                        console.error('[MyBookings] load more failed', error);
                      });
                    }}
                  >
                    {currentViewState.loadingMore ? t("Cargando…") : 'Ver más reservas'}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </main>
      </section>

      <AccessibleModal
        open={changeModalOpen}
        onClose={() => {
          setChangeModalOpen(false);
          setEditingBooking(null);
        }}
        labelledBy="change-modal-title"
        width={520}
      >
        <div className="mb-change-modal">
          <div className="mb-change-head">
            <div>
              <div className="mb-eyebrow">{t("Cambiar reserva")}</div>
              <h3 id="change-modal-title">
                {editingBooking ? (editingBooking.serviceName || editingBooking.title || "Servicio") : t("Cambiar reserva")}
              </h3>
              {editingBooking ? (
                <p>
                  {Number(editingBooking.duration || editingBooking.serviceDuration || editingBooking.durationMinutes || 60)} minutos
                  {editingBooking.businessName ? ` · ${editingBooking.businessName}` : ''}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => {
                setChangeModalOpen(false);
                setEditingBooking(null);
              }}
              className="mb-modal-close"
              aria-label={t("Cerrar")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mb-form-grid">
            <div className="mb-field">
              <label>{t("Fecha")}</label>
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            </div>

            <div className="mb-field">
              <label>{t("Hora")}</label>
              <div className="mb-time-row">
                <select value={newHour} onChange={(e) => setNewHour(e.target.value)} aria-label={t("Hora")}>
                  {hourOptions.map(h => <option key={h} value={h}>{h}</option>)}
                </select>

                <select value={newMinute} onChange={(e) => setNewMinute(e.target.value)} aria-label="Minutos">
                  {minuteOptions.map(mn => <option key={mn} value={mn}>{mn}</option>)}
                </select>
              </div>
              <small>{t("Intervalos de 30 minutos.")}</small>
            </div>

            {!availabilityCheck.ok ? (
              <div className="mb-availability is-error">
                {t(availabilityCheck.reason || "No disponible")}
              </div>
            ) : (
              <div className="mb-availability is-ok">
                {t(employeeDoc ? 'Horario disponible' : 'Verificando disponibilidad...')}
              </div>
            )}

            <div className="mb-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setChangeModalOpen(false);
                  setEditingBooking(null);
                }}
                className="mb-secondary-action"
              >{t("Cancelar")}</button>

              <button
                type="button"
                onClick={submitChange}
                disabled={savingChange || !availabilityCheck.ok}
                className="mb-primary-action"
              >
                {savingChange ? t("Enviando...") : t("Solicitar cambio")}
              </button>
            </div>
          </div>
        </div>
      </AccessibleModal>
    </div>
  );
}
